import { Button, Flex, ScrollArea } from "@radix-ui/themes";
import { z } from "zod";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout/page-header/PageHeader";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs, TabItem } from "@/v2/components/tabs/Tabs";
import { Params } from "./Params";
import { Body } from "./Body";
import { Authorization } from "./Authorization";
import { Headers } from "./Headers";
import {
  CurlRequest,
  AuthConfig,
  QueryParams,
} from "@/v2/entities/curl-request";
import { SimulatorWrapper } from "@/v2/features/simulator/context/SimulatorWrapper";
import { useGetSimulators } from "@/api/simulator/simulator.api";
import { JSONSchema7 } from "json-schema";
import { replaceArgsWithPlaceholders } from "./useBuildSkillForSimulator";
import { ImportFromCurl } from "./ImportFromCurl";
import { SkillUrl } from "./SkillUrl";
import { ChatWithSkill } from "./ChatWithSkill";
import {
  useMergeSchemas,
  useSplitSchema,
  useCurlRequestTransformer,
  useBuildJsonSchema,
} from "@/v2/entities/curl-request";
import {
  CreateSkillPayload,
  UpdateSkillPayload,
  useSkillApi,
} from "@/v2/entities/skill/Skill.api";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { buildSkillSlug, isBrowserSkill, Skill } from "@/v2/entities/skill/Skill.model";
import { toast } from "sonner";
import { InlineTextField } from "@/components/shared/inputs/inilie-text-field/InlineTextField";
import { FieldGroup } from "@/v2/components/fields/field-group/FieldGroup";
import { ArrowLeftIcon, CheckIcon, Pencil2Icon } from "@radix-ui/react-icons";
import { OptionalTextarea } from "@/features/automation-configuration/parts/OptionalTextarea";
import AiProgramming from "@/v2/assets/icons/AiProgramming";
import { useSkillId } from "./useSkillId";
import { ResponseFieldSelector } from "./ResponseFieldSelector";

const tabs = ["parameters", "authorization", "headers", "body", "response"] as const;
type Tab = (typeof tabs)[number];

function isTab(value: string): value is Tab {
  return tabs.includes(value as Tab);
}

const tabList: TabItem[] = tabs.map((tab) => ({
  value: tab,
  label: tab.charAt(0).toUpperCase() + tab.slice(1),
}));

const defaultCurlRequest: CurlRequest = {
  method: "GET",
  url: "",
  queryParams: {},
  headers: {},
  body: undefined,
  auth: undefined,
  options: undefined,
};

type SkillEditorPayload = {
  name: string;
  description: string;
  curlRequest: CurlRequest;
  bodyParamsSchema?: JSONSchema7;
  queryParamsSchema?: JSONSchema7;
  pathParamsSchema?: JSONSchema7;
};

interface SkillEditorProps {
  defaultSkill?: Skill;
  onSubmit: (skill: Skill) => Promise<void>;
  onTest: () => void;
  onDraft: (skill: Skill) => void;
}

function SkillEditor({
  defaultSkill,
  onSubmit,
  onTest,
  onDraft,
}: SkillEditorProps) {
  const navigate = useNavigate();
  const { parseCurl, buildCurl } = useCurlRequestTransformer();
  const mergeSchemas = useMergeSchemas();
  const splitSchema = useSplitSchema();
  const buildJsonSchema = useBuildJsonSchema();

  const [payload, setPayload] = useState<SkillEditorPayload>(() => {
    if (defaultSkill == null) {
      return {
        name: "New Skill",
        description: "",
        curlRequest: defaultCurlRequest,
      };
    }

    const { name, description, curl, parameters } = defaultSkill;
    const parameterSchema = isBrowserSkill(defaultSkill)
      ? undefined
      : (parameters as JSONSchema7 | undefined);

    if (isBrowserSkill(defaultSkill)) {
      return {
        name,
        description,
        curlRequest: defaultCurlRequest,
      };
    }

    const parsedCurl = parseCurl(curl ?? "");
    const { bodySchema, querySchema, pathSchema } = splitSchema(
      parsedCurl,
      parameterSchema ?? {}
    );

    return {
      name,
      description,
      curlRequest: parsedCurl,
      bodyParamsSchema: bodySchema,
      queryParamsSchema: querySchema,
      pathParamsSchema: pathSchema,
    };
  });
  const [responseFields, setResponseFields] = useState<string[]>(
    defaultSkill?.responseFields ?? []
  );
  const [selectedTab, setSelectedTab] = useState<Tab>("parameters");
  const [showImportFromCurl, setShowImportFromCurl] = useState(
    defaultSkill == null
  );

  const handleTabChange = useCallback((value: string) => {
    if (isTab(value)) {
      setSelectedTab(value);
    }
  }, []);

  const handleNameChange = useCallback((name: string) => {
    setPayload((prev) => ({ ...prev, name }));
  }, []);

  const handleDescriptionChange = useCallback((description: string) => {
    setPayload((prev) => ({ ...prev, description }));
  }, []);

  const handleResponseFieldsChange = useCallback((fields: string[]) => {
    setResponseFields(fields);
  }, []);

  const handleSubmit = useCallback(() => {
    // Build cURL without substituting path parameters (keep placeholders for the template)
    const newCurl = buildCurl(payload.curlRequest, { substitutePathParams: false });
    const parameters = mergeSchemas(
      payload.queryParamsSchema,
      payload.bodyParamsSchema,
      payload.pathParamsSchema
    );

    const parameterizedCurl = replaceArgsWithPlaceholders(newCurl, parameters);

    const newSkill: Skill = {
      id: "",
      name: payload.name,
      description: payload.description,
      curl: parameterizedCurl,
      parameters: parameters,
      slug: buildSkillSlug(payload.name),
      quackUsage: 0,
      requiresApproval: false,
      responseFields: responseFields.length ? responseFields : null,
    };

    onSubmit(newSkill);
  }, [payload, onSubmit, buildCurl, replaceArgsWithPlaceholders, mergeSchemas, responseFields]);

  const handleTest = useCallback(() => {
    onTest();
  }, [onTest]);

  const handleDraft = useCallback(
    (payload: SkillEditorPayload) => {
      const newCurl = buildCurl(payload.curlRequest, { substitutePathParams: false });
      const parameters = mergeSchemas(
        payload.queryParamsSchema,
        payload.bodyParamsSchema,
        payload.pathParamsSchema
      );

      const parameterizedCurl = replaceArgsWithPlaceholders(
        newCurl,
        parameters
      );

      const newSkill: Skill = {
        id: "",
        name: payload.name,
        description: payload.description,
        curl: parameterizedCurl,
        parameters: parameters,
        slug: buildSkillSlug(payload.name),
        quackUsage: 0,
        requiresApproval: false,
        responseFields: responseFields.length ? responseFields : null,
      };

      onDraft(newSkill);
    },
    [onDraft, buildCurl, replaceArgsWithPlaceholders, mergeSchemas, responseFields]
  );

  const handleCurlRequestChange = useCallback(
    (curlRequest: CurlRequest) => {
      setPayload((prev) => {
        // Generate or update path parameters schema if path parameters are detected
        let pathParamsSchema = prev.pathParamsSchema;
        
        if (curlRequest.pathParams && Object.keys(curlRequest.pathParams).length > 0) {
          // Start with existing schema or create new one
          pathParamsSchema = pathParamsSchema || {
            type: "object",
            properties: {},
            required: [],
          };
          
          // Ensure properties object exists
          if (!pathParamsSchema.properties) {
            pathParamsSchema.properties = {};
          }
          if (!Array.isArray(pathParamsSchema.required)) {
            pathParamsSchema.required = [];
          }
          
          // Add new path parameters, preserving existing ones
          Object.keys(curlRequest.pathParams).forEach((key) => {
            if (!pathParamsSchema!.properties![key]) {
              pathParamsSchema!.properties![key] = {
                type: "string",
                description: `Path parameter - ${key}`,
              };
              if (!pathParamsSchema!.required!.includes(key)) {
                pathParamsSchema!.required!.push(key);
              }
            }
          });
          
          // Remove path parameters that are no longer in the URL
          const currentPathParamKeys = Object.keys(curlRequest.pathParams);
          if (pathParamsSchema.properties) {
            Object.keys(pathParamsSchema.properties).forEach((key) => {
              if (!currentPathParamKeys.includes(key)) {
                delete pathParamsSchema!.properties![key];
                pathParamsSchema!.required = pathParamsSchema!.required!.filter(req => req !== key);
              }
            });
          }
        } else if (!curlRequest.pathParams || Object.keys(curlRequest.pathParams).length === 0) {
          // Clear path parameters schema if no path parameters
          pathParamsSchema = undefined;
        }
        
        return { 
          ...prev, 
          curlRequest, 
          pathParamsSchema 
        };
      });
    },
    [setPayload]
  );

  const handleQueryParamsChange = useCallback(
    (queryParams: QueryParams) => {
      setPayload((prev) => {
        const newCurlRequest = { ...prev.curlRequest, queryParams };

        // Update URL with query parameters
        const baseUrl = newCurlRequest.url.split("?")[0];
        const queryString = new URLSearchParams(
          Object.entries(queryParams).flatMap(([k, v]) =>
            Array.isArray(v) ? v.map((val) => [k, val]) : [[k, v]]
          )
        ).toString();

        const newUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
        const curlRequest = { ...newCurlRequest, url: newUrl };

        return { ...prev, curlRequest };
      });
    },
    [setPayload]
  );

  const handleQueryParamsSchemaChange = useCallback(
    (queryParamsSchema: JSONSchema7) => {
      setPayload((prev) => ({ ...prev, queryParamsSchema }));
    },
    [setPayload]
  );

  const handleBodySchemaChange = useCallback(
    (bodySchema: JSONSchema7) => {
      setPayload((prev) => ({
        ...prev,
        bodyParamsSchema: bodySchema,
      }));
    },
    [setPayload]
  );

  const handlePathParamsSchemaChange = useCallback(
    (pathParamsSchema: JSONSchema7) => {
      setPayload((prev) => ({ ...prev, pathParamsSchema }));
    },
    [setPayload]
  );

  const handleAuthConfigChange = useCallback(
    (authConfig: AuthConfig | undefined) => {
      setPayload((prev) => ({
        ...prev,
        curlRequest: { ...prev.curlRequest, auth: authConfig },
      }));
    },
    [setPayload]
  );

  const handleHeadersChange = useCallback(
    (headers: Record<string, string>) => {
      setPayload((prev) => ({
        ...prev,
        curlRequest: { ...prev.curlRequest, headers },
      }));
    },
    [setPayload]
  );

  const handlePathParamsChange = useCallback(
    (pathParams: Record<string, string>) => {
      setPayload((prev) => {
        const currentUrl = prev.curlRequest.url;
        const urlParts = currentUrl.split("?");
        let baseUrl = urlParts[0];
        const queryString = urlParts[1];

        // Get existing placeholders in the URL
        const existingPlaceholders = (baseUrl.match(/\{[^}]+\}/g) || [])
          .map(p => p.slice(1, -1)); // Remove { and }
        
        if (pathParams && Object.keys(pathParams).length > 0) {
          const newParamKeys = Object.keys(pathParams);
          
          // Remove placeholders that are no longer in pathParams
          existingPlaceholders.forEach(placeholder => {
            if (!newParamKeys.includes(placeholder)) {
              baseUrl = baseUrl.replace(new RegExp(`/\\{${placeholder}\\}`, 'g'), '');
            }
          });
          
          // Add missing placeholders to the URL
          const remainingPlaceholders = (baseUrl.match(/\{[^}]+\}/g) || [])
            .map(p => p.slice(1, -1));
          const missingParams = newParamKeys.filter(key => !remainingPlaceholders.includes(key));
          if (missingParams.length > 0) {
            const newPlaceholders = missingParams.map(key => `{${key}}`);
            if (!baseUrl.endsWith('/') && baseUrl !== '') {
              baseUrl += '/';
            }
            baseUrl += newPlaceholders.join('/');
          }
        } else {
          // No path parameters - remove all placeholders from URL
          baseUrl = baseUrl.replace(/\/\{[^}]+\}/g, '');
        }
        
        // Clean up any trailing slashes or double slashes
        baseUrl = baseUrl.replace(/\/+/g, '/').replace(/\/$/, '') || baseUrl;

        const newUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return {
          ...prev,
          curlRequest: { 
            ...prev.curlRequest, 
            pathParams: Object.keys(pathParams).length > 0 ? pathParams : undefined,
            url: newUrl
          },
        };
      });
    },
    [setPayload]
  );

  const handleShowImportFromCurl = useCallback(() => {
    setShowImportFromCurl(true);
  }, [setShowImportFromCurl]);

  const handleCloseImportFromCurl = useCallback(() => {
    setShowImportFromCurl(false);
  }, [setShowImportFromCurl]);

  useEffect(() => {
    handleDraft(payload);
  }, [payload, handleDraft]);

  const handleImportFromCurl = useCallback(
    (curl: string) => {
      const parsedCurl = parseCurl(curl);
      const schema = buildJsonSchema(parsedCurl.body ?? {});
      const { bodySchema, querySchema, pathSchema } = splitSchema(
        parsedCurl,
        schema
      );

      setPayload({
        name: "New Skill",
        description: "",
        curlRequest: parsedCurl,
        bodyParamsSchema: bodySchema,
        queryParamsSchema: querySchema,
        pathParamsSchema: pathSchema,
      });

      setShowImportFromCurl(false);
    },
    [parseCurl, buildJsonSchema, splitSchema, setPayload, setShowImportFromCurl]
  );

  const {
    name,
    curlRequest,
    queryParamsSchema,
    bodyParamsSchema,
    pathParamsSchema,
  } = payload;

  return (
    <>
      <ScrollArea scrollbars="vertical" style={{ height: "100%" }}>
        <Flex
          maxWidth={"100%"}
          flexGrow="1"
          px="6"
          direction="column"
          gap={"4"}
        >
          <Flex pl="6" pt="2">
            <Button
              variant="soft"
              color="gray"
              onClick={() => navigate("/automation-center/skills")}
            >
              <ArrowLeftIcon />
              Back
            </Button>
          </Flex>

          <PageHeader
            title="Skill Lab"
            icon={<AiProgramming size={24} fill="orange" />}
            actions={
              <Button
                variant="solid"
                color="gray"
                highContrast
                onClick={handleSubmit}
              >
                <CheckIcon />
                {defaultSkill == null ? "Create" : "Update"}
              </Button>
            }
          />

          <FieldGroup
            label="Name"
            noSeparator
            classes={{ wrapper: "p-0", root: "!gap-1" }}
          >
            <InlineTextField
              size="2"
              value={name}
              onChange={handleNameChange}
              icon={<Pencil2Icon className="ml-2" />}
            />
          </FieldGroup>

          <FieldGroup
            label="Description"
            classes={{ wrapper: "p-0 pb-4 mb-4 max-w-[600px]", root: "!gap-1" }}
          >
            <OptionalTextarea
              value={payload.description}
              onChange={handleDescriptionChange}
              buttonText="Add Description"
              optional={false}
            />
          </FieldGroup>

          <SkillUrl
            value={curlRequest}
            onChange={handleCurlRequestChange}
            onImportFromCurl={handleShowImportFromCurl}
            onTest={handleTest}
            loadingSimulators={false}
          />

          <Tabs
            tabs={tabList}
            value={selectedTab}
            onValueChange={handleTabChange}
          />

          <div className="flex-1 mt-4">
            {selectedTab === "parameters" && (
              <Params
                queryParams={curlRequest.queryParams}
                queryParamsSchema={queryParamsSchema}
                pathParams={curlRequest.pathParams}
                pathParamsSchema={pathParamsSchema}
                onQueryParamsChange={handleQueryParamsChange}
                onQueryParamsSchemaChange={handleQueryParamsSchemaChange}
                onPathParamsChange={handlePathParamsChange}
                onPathParamsSchemaChange={handlePathParamsSchemaChange}
              />
            )}
            {selectedTab === "authorization" && (
              <Authorization
                authConfig={curlRequest.auth}
                onAuthConfigChange={handleAuthConfigChange}
              />
            )}
            {selectedTab === "headers" && (
              <Headers
                headers={curlRequest.headers}
                onHeadersChange={handleHeadersChange}
              />
            )}
            {selectedTab === "body" && (
              <Body
                schema={bodyParamsSchema}
                onValueChange={handleBodySchemaChange}
              />
            )}
            {selectedTab === "response" && (
              <ResponseFieldSelector
                skillId={defaultSkill?.id}
                parameters={defaultSkill?.parameters}
                sampleResponse={defaultSkill?.sampleResponse}
                responseFields={responseFields}
                onResponseFieldsChange={handleResponseFieldsChange}
              />
            )}
          </div>
        </Flex>
      </ScrollArea>

      {showImportFromCurl && (
        <ImportFromCurl
          onClose={handleCloseImportFromCurl}
          onImport={handleImportFromCurl}
        />
      )}
    </>
  );
}

export function CreateSkillLab() {
  const navigate = useNavigate();
  const skillApi = useSkillApi();

  const [simulatorId, setSimulatorId] = useState<string | undefined>();
  const [draft, setDraft] = useState<Skill | undefined>();
  const [showChat, setShowChat] = useState(false);

  const skillsOverride = useMemo(() => {
    return draft ? [draft] : [];
  }, [draft]);

  const { data: simulators, isLoading: loadingSimulators } = useGetSimulators(
    (data) => {
      setSimulatorId(data[0].id.toString());
    }
  );

  const { mutateAsync: createSkill } = useMutation({
    mutationKey: ["createSkill"],
    mutationFn: skillApi.createSkill,
  });

  const handleCreate = useCallback(
    async (skill: Skill) => {
      const missingFields: string[] = [];
      if (!skill.name?.trim()) missingFields.push("name");
      if (!skill.description?.trim()) missingFields.push("description");
      if (missingFields.length > 0) {
        toast.error(`Please fill in the ${missingFields.join(" and ")} for the skill`);
        return;
      }

      const createSkillPayload: CreateSkillPayload = {
        name: skill.name,
        description: skill.description,
        curl: skill.curl,
        slug: skill.slug,
        parameters: skill.parameters,
        ...(skill.responseFields?.length ? { responseFields: skill.responseFields } : {}),
      };

      toast.promise(createSkill(createSkillPayload), {
        loading: "Creating skill...",
        success: ({ name }) => {
          navigate(`/automation-center/skills`);

          return {
            message: `${name} created successfully`,
          };
        },
        error: () => ({
          message: "Failed to create skill",
        }),
      });
    },
    [skillApi, navigate, createSkill]
  );

  const handleTest = useCallback(() => {
    setShowChat(true);
  }, [setShowChat]);

  const handleDraft = useCallback(
    (skill: Skill) => {
      setDraft(skill);
    },
    [setDraft]
  );

  if (loadingSimulators || !simulatorId) {
    return <></>;
  }

  return (
    <SimulatorWrapper simulatorId={simulatorId} skillsOverride={skillsOverride}>
      <PageLayoutV2>
        <Flex height="100%" maxWidth={"100vw"} maxHeight={"100vh"}>
          <SkillEditor
            onSubmit={handleCreate}
            onTest={handleTest}
            onDraft={handleDraft}
          />

          {showChat && (
            <ChatWithSkill
              simulatorId={simulatorId}
              onSimulatorChange={setSimulatorId}
              simulators={simulators}
              loading={loadingSimulators}
            />
          )}
        </Flex>
      </PageLayoutV2>
    </SimulatorWrapper>
  );
}

export function EditSkillLab() {
  const navigate = useNavigate();
  const skillId = useSkillId();
  const skillApi = useSkillApi();

  const [simulatorId, setSimulatorId] = useState<string | undefined>();
  const [draft, setDraft] = useState<Skill | undefined>();
  const [showChat, setShowChat] = useState(false);

  const { data: skill, isLoading: loadingSkill } = useQuery({
    queryKey: ["skill", skillId],
    queryFn: () => skillApi.getSkill(skillId),
    enabled: skillId != null,
    onSuccess: (data) => {
      setDraft(data);
    },
  });

  const skillsOverride = useMemo(() => {
    return draft ? [draft] : [];
  }, [draft]);

  const { data: simulators, isLoading: loadingSimulators } = useGetSimulators(
    (data) => {
      setSimulatorId(data[0].id.toString());
    }
  );

  const { mutateAsync: updateSkill } = useMutation({
    mutationKey: ["updateSkill"],
    mutationFn: skillApi.updateSkill,
  });

  const handleUpdate = useCallback(
    async (skill: Skill) => {
      const missingFields: string[] = [];
      if (!skill.name?.trim()) missingFields.push("name");
      if (!skill.description?.trim()) missingFields.push("description");
      if (missingFields.length > 0) {
        toast.error(`Please fill in the ${missingFields.join(" and ")} for the skill`);
        return;
      }

      const updateSkillPayload: UpdateSkillPayload = {
        id: skillId,
        name: skill.name,
        description: skill.description,
        curl: skill.curl,
        parameters: skill.parameters,
        ...(skill.responseFields?.length ? { responseFields: skill.responseFields } : {}),
      };

      toast.promise(updateSkill(updateSkillPayload), {
        loading: "Updating skill...",
        success: ({ name }) => {
          navigate(`/automation-center/skills`);

          return {
            message: `${name} updated successfully`,
          };
        },
        error: () => ({
          message: "Failed to update skill",
        }),
      });
    },
    [skillApi, skillId, navigate, updateSkill]
  );

  const handleTest = useCallback(() => {
    setShowChat(true);
  }, [setShowChat]);

  const handleDraft = useCallback(
    (skill: Skill) => {
      setDraft(skill);
    },
    [setDraft]
  );

  if (loadingSimulators || !simulatorId || loadingSkill) {
    return <></>;
  }

  return (
    <SimulatorWrapper simulatorId={simulatorId} skillsOverride={skillsOverride}>
      <PageLayoutV2>
        <Flex
          height="100%"
          maxWidth={"100vw"}
          maxHeight={"100vh"}
          overflow="hidden"
        >
          <SkillEditor
            defaultSkill={skill}
            onSubmit={handleUpdate}
            onTest={handleTest}
            onDraft={handleDraft}
          />

          {showChat && (
            <ChatWithSkill
              simulatorId={simulatorId}
              onSimulatorChange={setSimulatorId}
              simulators={simulators}
              loading={loadingSimulators}
            />
          )}
        </Flex>
      </PageLayoutV2>
    </SimulatorWrapper>
  );
}
